import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Logo, LogoMark } from "../components/brand/Logo";
import { useAuth } from "../../lib/auth/useAuth";
import { isSupabaseConfigured } from "../../lib/supabase";
import { trackEvent } from "../../lib/telemetry/analytics";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

const PANEL_QUOTES = [
  {
    q: "We stopped dreading tax season. Proofly just keeps our books ready.",
    who: "Maya R. · Salt & Fig Bakery",
    initials: "MR",
  },
  {
    q: "The audit score is the only dashboard my accountant actually opens.",
    who: "Daniel K. · Oakline HVAC",
    initials: "DK",
  },
  {
    q: "Free for 9 seats was the unlock. Connected QuickBooks in one click.",
    who: "Priya S. · Lantern Studio",
    initials: "PS",
  },
];

type SubmitState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

type GoogleState =
  | { kind: "idle" }
  | { kind: "redirecting" }
  | { kind: "error"; message: string };

function GoogleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
    >
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.04-3.7H.96v2.32A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.96 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.96H.96A9 9 0 0 0 0 9a9 9 0 0 0 .96 4.04l3-2.32z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.34l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96l3 2.32C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}

/** Tiny trend sparkline for the audit-score widget. Static + token-driven. */
function AuditTrend() {
  // Mock score trajectory trending up toward 96. Pure visual — no data hook.
  const points = [
    [0, 22],
    [14, 18],
    [28, 20],
    [42, 14],
    [56, 12],
    [70, 8],
    [84, 6],
    [100, 4],
  ];
  const path = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`)
    .join(" ");
  return (
    <svg
      viewBox="0 0 100 28"
      width="100%"
      height="28"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <path
        d={path}
        fill="none"
        stroke="var(--brand-600)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AvatarBadge({ initials }: { initials: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full text-white text-[11px] font-semibold shrink-0"
      style={{
        width: 32,
        height: 32,
        background: "linear-gradient(135deg, #4F6BFF 0%, #8B5CF6 100%)",
        letterSpacing: "0.02em",
      }}
    >
      {initials}
    </span>
  );
}

export function Login() {
  const { signInWithMagicLink, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });
  const [googleState, setGoogleState] = useState<GoogleState>({ kind: "idle" });
  const [emailOpen, setEmailOpen] = useState(false);

  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setQuoteIdx((i) => (i + 1) % PANEL_QUOTES.length),
      5400,
    );
    return () => clearInterval(id);
  }, []);

  async function handleGoogle() {
    if (!isSupabaseConfigured && !E2E_BYPASS) {
      setGoogleState({
        kind: "error",
        message: "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
      });
      return;
    }
    setGoogleState({ kind: "redirecting" });
    try {
      await signInWithGoogle();
      trackEvent("sign_in", { method: "google" });
      if (E2E_BYPASS) {
        navigate("/inbox", { replace: true });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign-in failed";
      setGoogleState({ kind: "error", message });
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setState({ kind: "error", message: "Please enter an email address." });
      return;
    }
    if (!isSupabaseConfigured) {
      setState({
        kind: "error",
        message: "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
      });
      return;
    }
    setState({ kind: "sending" });
    try {
      const { error } = await signInWithMagicLink(trimmed);
      if (error) {
        setState({ kind: "error", message: error.message });
        return;
      }
      trackEvent("sign_in", { method: "magic_link" });
      setState({ kind: "sent" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign-in failed";
      setState({ kind: "error", message });
    }
  }

  const sending = state.kind === "sending";
  const redirecting = googleState.kind === "redirecting";
  const quote = PANEL_QUOTES[quoteIdx];

  return (
    <div className="min-h-screen bg-white grid md:grid-cols-[1fr_1fr] lg:grid-cols-[1.05fr_1fr]">
      {/* ───────── Left brand panel (cream, warm) ───────── */}
      <section
        className="relative hidden md:flex flex-col justify-between overflow-hidden p-10 lg:p-14 border-r border-[var(--ink-200)]"
        style={{
          background: "var(--ink-50)",
          backgroundImage:
            "linear-gradient(var(--ink-100) 1px, transparent 1px), linear-gradient(90deg, var(--ink-100) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          backgroundPosition: "-1px -1px",
        }}
      >
        {/* Soft fade so the grid reads as texture, not a ruled page */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 500px at 30% 20%, rgba(255,255,255,0.85), transparent 70%), linear-gradient(180deg, rgba(255,255,255,0.4), rgba(255,255,255,0.8))",
          }}
        />

        <div className="relative z-10">
          <Link to="/" aria-label="Proofly home" className="inline-block">
            <Logo size={36} />
          </Link>
        </div>

        {/* Middle column: audit widget + testimonial */}
        <div className="relative z-10 max-w-[420px] space-y-6">
          {/* Audit score widget */}
          <div className="rounded-2xl bg-white border border-[var(--ink-200)] p-5 shadow-[0_1px_2px_rgba(10,15,30,0.04),0_8px_24px_-12px_rgba(10,15,30,0.08)]">
            <div className="flex items-baseline justify-between">
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[var(--ink-500)]">
                Audit score
              </div>
              <div className="text-[11px] font-medium text-emerald-600">
                +4 this week
              </div>
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span
                className="font-display font-semibold tracking-[-0.02em] text-[var(--ink-950)]"
                style={{ fontSize: "38px", lineHeight: 1 }}
              >
                96
              </span>
              <span className="text-[15px] text-[var(--ink-500)]">/ 100</span>
            </div>
            <div className="mt-3">
              <AuditTrend />
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--ink-100)]">
              <div className="text-[12px] font-medium text-[var(--ink-700)] mb-2">
                2 items need attention
              </div>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2 text-[13px] text-[var(--ink-700)]">
                  <span
                    aria-hidden="true"
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--warning,#F59E0B)]"
                    style={{ background: "#F59E0B" }}
                  />
                  3 receipts missing a category
                </li>
                <li className="flex items-center gap-2 text-[13px] text-[var(--ink-700)]">
                  <span
                    aria-hidden="true"
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: "#F59E0B" }}
                  />
                  Q1 mileage log unreviewed
                </li>
              </ul>
            </div>
          </div>

          {/* Rotating testimonial */}
          <figure
            key={quoteIdx}
            className="rounded-2xl bg-white border border-[var(--ink-200)] p-5"
            style={{ animation: "proofly-rotate-in 520ms ease-out both" }}
          >
            <blockquote className="text-[14px] leading-relaxed text-[var(--ink-900)]">
              "{quote.q}"
            </blockquote>
            <figcaption className="mt-3 flex items-center gap-2.5">
              <AvatarBadge initials={quote.initials} />
              <span className="text-[12px] text-[var(--ink-600)]">
                {quote.who}
              </span>
            </figcaption>
          </figure>
        </div>

        <div className="relative z-10 flex items-center gap-5 text-[12px] text-[var(--ink-500)]">
          <span>proofly.one</span>
          <span aria-hidden="true">·</span>
          <Link to="/pricing" className="hover:text-[var(--ink-900)]">
            Pricing
          </Link>
          <Link to="/features" className="hover:text-[var(--ink-900)]">
            Features
          </Link>
        </div>
      </section>

      {/* ───────── Right sign-in panel ───────── */}
      <section className="flex flex-col bg-white">
        <header className="px-6 md:px-10 py-5 md:hidden flex items-center justify-between">
          <Link to="/" aria-label="Proofly home">
            <Logo size={28} />
          </Link>
          <Link
            to="/"
            className="text-sm font-medium text-[var(--ink-700)] hover:text-[var(--ink-900)]"
          >
            Back
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-[400px]">
            {/* Mobile-only mark above the heading */}
            <div className="md:hidden flex justify-center mb-6">
              <LogoMark size={56} />
            </div>

            {/* Free-forever chip pinned above the title */}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ink-200)] bg-[var(--ink-50)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-700)]">
              <span
                aria-hidden="true"
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "#10B981" }}
              />
              Free forever for teams under 10
            </span>

            <h1 className="mt-4 text-[30px] md:text-[36px] font-display font-semibold tracking-[-0.025em] text-[var(--ink-950)] leading-[1.05]">
              Welcome back.
            </h1>
            <p className="mt-2 text-[15px] text-[var(--ink-700)]">
              Sign in to keep your records audit-ready.
            </p>

            <Button
              type="button"
              data-testid="login-google"
              onClick={handleGoogle}
              disabled={redirecting}
              className="mt-7 w-full h-12 bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white flex items-center justify-center gap-2.5 rounded-xl text-[15px] font-semibold shadow-[0_10px_24px_-12px_rgba(46,91,255,0.5)] transition-all"
            >
              <span className="inline-flex items-center justify-center rounded-sm bg-white p-0.5">
                <GoogleIcon />
              </span>
              {redirecting ? "Redirecting…" : "Continue with Google"}
            </Button>

            {googleState.kind === "error" && (
              <div
                role="alert"
                className="mt-3 rounded-md border border-[var(--danger-50)] bg-[var(--danger-50)] px-3 py-2 text-sm text-[var(--danger)]"
              >
                {googleState.message}
              </div>
            )}

            <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.12em] text-[var(--ink-500)]">
              <div className="h-px flex-1 bg-[var(--ink-200)]" />
              <span>or</span>
              <div className="h-px flex-1 bg-[var(--ink-200)]" />
            </div>

            <button
              type="button"
              data-testid="login-email-toggle"
              onClick={() => setEmailOpen((v) => !v)}
              className="mt-4 w-full text-center text-sm font-medium text-[var(--ink-700)] hover:text-[var(--ink-900)] underline underline-offset-4 decoration-[var(--ink-300)] hover:decoration-[var(--ink-700)]"
              aria-expanded={emailOpen}
              aria-controls="login-email-panel"
            >
              {emailOpen ? "Hide email sign-in" : "Sign in with email"}
            </button>

            {emailOpen && (
              <form id="login-email-panel" onSubmit={handleSubmit} className="space-y-3 mt-5">
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-[var(--ink-700)] mb-1.5">
                    Email
                  </label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    disabled={sending}
                    className="h-11"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit"
                  disabled={sending}
                  className="w-full h-11 bg-[var(--ink-950)] hover:bg-[var(--ink-900)] text-white rounded-xl"
                >
                  {sending ? "Sending…" : "Send magic link"}
                </Button>

                {state.kind === "sent" && (
                  <div
                    role="status"
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                  >
                    Check your inbox for the sign-in link.
                  </div>
                )}
                {state.kind === "error" && (
                  <div
                    role="alert"
                    className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  >
                    {state.message}
                  </div>
                )}
              </form>
            )}

            <p className="mt-8 text-center text-[13px] text-[var(--ink-500)]">
              New to Proofly?{" "}
              <Link
                to="/"
                className="font-semibold text-[var(--ink-950)] hover:text-[var(--brand-700)]"
              >
                See what you get free →
              </Link>
            </p>
          </div>
        </div>

        <footer className="px-6 md:px-10 py-6 text-xs text-[var(--ink-500)] flex flex-wrap gap-x-4 gap-y-1">
          <span>© {new Date().getFullYear()} Proofly</span>
          <Link to="/terms" className="hover:text-[var(--ink-900)]">Terms</Link>
          <Link to="/privacy" className="hover:text-[var(--ink-900)]">Privacy</Link>
        </footer>
      </section>
    </div>
  );
}
